import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import EntriesDropdown from "../../Common/TableComponent/EntriesDropdown";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { fetchProducts, deleteProduct } from "../../../services/productService";
import { Loading } from "../../Common/OtherElements/Loading";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ProductToggleSwitch from "./ProductToggleSwitch";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import ProductGalleryModal from "../ProductGallery/ProductGalleryModal";
import * as XLSX from "xlsx";
import allImages from "../../../assets/images-import";

const ViewProduct = () => {
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [manageProducts, setManageProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageAccessDetails, setPageAccessDetails] = useState([]);
  const PageLevelAccessurl = "category";
  const navigate = useNavigate();
  const [downloadBtnDisable, setDownloadBtnDisable] = useState(false);
  const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);
  const [showModal, setShowModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState({
    productId: null,
    productName: "",
  });

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    } else {
      console.log("No page access details found");
    }
  }, [pageAccessData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const response = await fetchProducts();
        if (response.isSuccess) {
          setManageProducts(response.result);
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = manageProducts.filter(
    (products) =>
      products.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      products.categoryName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      products.subCategoryName
        .toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      products.brandName.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage);

  const handleDelete = async (productId) => {
    const confirmed = await confirmDelete("product");
    if (confirmed) {
      try {
        await deleteProduct(productId);
        setManageProducts((prev) =>
          prev.filter((item) => item.id !== productId)
        );
        Swal.fire("Deleted!", "Product deleted successfully!", "success");
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  const handleStatusChange = (id, newStatus) => {
    setManageProducts((prevProperties) =>
      prevProperties.map((property) =>
        property.id === id ? { ...property, status: newStatus } : property
      )
    );
  };

  // Updated handleGalleryUpload function in ViewProduct.jsx
  const handleGalleryUpload = async (responseData = null) => {
    try {
      toast.success("Gallery images uploaded successfully!");
    } catch (error) {
      console.error("Error in handleGalleryUpload:", error);
      handleErrors(error);
    }
  };

  const handleDownloadReport = async () => {
    try {
      // disable button while exporting
      setDownloadBtnDisable(true);

      // Make sure we have data to export; fallback to empty array to avoid errors
      const rows = Array.isArray(manageProducts) ? manageProducts : [];

      // Map manageProducts to friendly Excel rows
      const excelData = rows.map((item, index) => ({
        "#": index + 1,
        ID: item.id ?? "",
        Category: item.categoryName ?? "",
        SubCategory: item.subCategoryName ?? "",
        Brand: item.brandName ?? "",
        "HSN Code": item.hsnCode ?? item.hsnCodeId ?? "",
        SKU: item.skuCode ?? "",
        "Product Name": item.productName ?? "",
        "Product URL": item.productURL ?? item.productUrl ?? "",
        Origin: item.origin ?? "",
        Published: item.published === true ? "Yes" : "No",
        "Product Tags": item.productTags ?? "",
        "Product Order": item.productOrder ?? "",
        "Created On": item.createdOn
          ? new Date(item.createdOn).toLocaleString()
          : "",
        "Created By": item.createdByUserName ?? "",
        "Modified On": item.modifiedOn
          ? new Date(item.modifiedOn).toLocaleString()
          : "",
        "Modified By": item.modifiedByUserName ?? "",
      }));

      // Create worksheet & workbook
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Products");

      // make filename with timestamp
      const pad = (n) => String(n).padStart(2, "0");
      const d = new Date();
      const filename = `products_${d.getFullYear()}${pad(
        d.getMonth() + 1
      )}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}${pad(
        d.getSeconds()
      )}.xlsx`;

      // Trigger download
      XLSX.writeFile(workbook, filename);
    } catch (error) {
      // use your existing error handler
      handleErrors(error);
    } finally {
      // re-enable button
      setDownloadBtnDisable(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <ComponentHeader title="View Products" />
      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header responsive-filter-type justify-content-between">
                <h5 className="mb-sm-2 mt-sm-2">Manage Products</h5>
                {pageAccessDetails.downloadAccess ? (
                  <button
                    className="btn btn-download"
                    onClick={handleDownloadReport}
                    disabled={downloadBtnDisable}
                  >
                    <img
                      src={allImages.Excel}
                      alt="Flat Report Export to excel"
                      width="24px"
                    />{" "}
                    <span>
                      {downloadBtnDisable ? "Exporting..." : "Export To Excel"}
                    </span>
                  </button>
                ) : (
                  ""
                )}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <div className="pagination-details-responsive justify-content-between mb-3">
                  <EntriesDropdown
                    entriesPerPage={entriesPerPage}
                    onEntriesChange={handleEntriesChange}
                    options={[10, 25, 50, 100]}
                  />
                  <div>
                    <input
                      type="text"
                      placeholder="Search..."
                      className="form-control mb-2"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "#",
                          "Catalogue",
                          "Product",
                          "Product Variant",
                          "Product FAQ",
                          "Product Gallery",
                          "Published?",
                          "Modified On",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-property-owner-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="8" />
                        ) : (
                          currentData.map((item, index) => (
                            <TablesRow
                              key={item.id}
                              rowData={{
                                Id:
                                  (currentPage - 1) * entriesPerPage +
                                  index +
                                  1,
                                catalogue: (
                                  <div className=" p-2 fs-12">
                                    <span className="text-start d-block mb-2">
                                      <strong>Brand:</strong> {item.brandName}
                                    </span>
                                    <span className="text-start d-block mb-2">
                                      <strong>Category:</strong>{" "}
                                      {item.categoryName}
                                    </span>
                                    <span className="text-start d-block">
                                      <strong>SubCategory:</strong>{" "}
                                      {item.subCategoryName}
                                    </span>
                                  </div>
                                ),
                                publishOrUnPublish: (
                                  <ProductToggleSwitch
                                    propId={item.id}
                                    initialStatus={
                                      item.published === true
                                        ? "Published"
                                        : "UnPublished"
                                    }
                                    onStatusChange={(newStatus) =>
                                      handleStatusChange(item.id, newStatus)
                                    }
                                  />
                                ),
                                productName: item.productName,
                                productVariant: (
                                  <i
                                    className="mdi mdi-archive-plus f-20 "
                                    style={{
                                      cursor: "pointer",
                                      color: "#13096a",
                                    }}
                                    onClick={() =>
                                      navigate(`${item.id}/variant`)
                                    }
                                  ></i>
                                ),
                                productFaq: (
                                  <i
                                    className="mdi mdi-chat-question f-20 "
                                    style={{
                                      cursor: "pointer",
                                      color: "#13096a",
                                    }}
                                    onClick={() => navigate(`${item.id}/faq`)}
                                  ></i>
                                ),
                                productGallery: pageAccessDetails.editAccess ? (
                                  <span
                                    className="badge text-bg-primary text-white"
                                    style={{ fontSize: "12px" }}
                                  >
                                    <Link
                                      style={{ color: "#fff" }}
                                      onClick={() => {
                                        setSelectedProduct({
                                          productId: item.id,
                                          productName: item.productName,
                                        });
                                        setShowModal(true);
                                      }}
                                    >
                                      Add Gallery
                                    </Link>
                                  </span>
                                ) : (
                                  <span style={{ color: "#dc3545" }}>
                                    Forbidden
                                  </span>
                                ),
                                modifiedOn: (
                                  <div>
                                    {new Date(item.modifiedOn)
                                      .toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        second: "2-digit",
                                        hour12: true,
                                      })
                                      .replace(",", "")}
                                    <br />
                                    <span className="text-muted fs-10">
                                      modified by {item.modifiedByUserName}
                                    </span>
                                  </div>
                                ),
                              }}
                              columns={[
                                "Id",
                                "catalogue",
                                "productName",
                                "productVariant",
                                "productFaq",
                                "productGallery",
                                "publishOrUnPublish",
                                "modifiedOn",
                              ]}
                              hideIcons={false}
                              onEdit={() => navigate(`update/${item.id}`)}
                              onDelete={() => handleDelete(item.id)}
                              pageLevelAccessData={pageAccessDetails}
                            />
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
      <ProductGalleryModal
        show={showModal}
        onHide={() => setShowModal(false)}
        productId={selectedProduct.productId}
        productName={selectedProduct.productName}
        onReset={handleGalleryUpload}
      />
    </>
  );
};

export default ViewProduct;
